<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// Payload do endpoint conversion_funnel (livro de eventos): array ordenado
// por posição com passagens reais — entered/exited_won/exited_lost/remaining.
// Antes o componente mostrava a foto da etapa ATUAL (by_stage), que não é
// conversão: card ganho "sumia" das etapas por onde passou.
const stages = computed(() => {
  if (!Array.isArray(props.data?.stages)) return [];
  return props.data.stages.map(stage => ({
    key: stage.stage,
    label: stage.label,
    color: stage.color,
    total: stage.entered,
    won: stage.exited_won,
    lost: stage.exited_lost,
    open: stage.remaining,
    conversion_rate: stage.conversion_from_start,
  }));
});

const maxTotal = computed(() => {
  if (!stages.value.length) return 1;
  return Math.max(...stages.value.map(s => s.total || 0), 1);
});

const getBarWidth = total => {
  const percentage = (total / maxTotal.value) * 100;
  return `${Math.max(percentage, 5)}%`;
};
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-6">
      {{ t('DEAL_REPORTS.FUNNEL.TITLE') }}
    </h3>

    <div v-if="stages.length" class="space-y-3">
      <div v-for="stage in stages" :key="stage.key" class="relative">
        <div class="flex items-center gap-3">
          <!-- Stage bar -->
          <div
            class="h-10 rounded-lg flex items-center px-3 transition-all duration-300"
            :style="{
              width: getBarWidth(stage.total),
              backgroundColor: stage.color || '#6b7280',
            }"
          >
            <span class="text-white text-sm font-medium truncate">
              {{ stage.label || stage.key }}
            </span>
          </div>

          <!-- Stats -->
          <div class="flex items-center gap-4 text-sm">
            <span class="text-slate-900 dark:text-white font-medium">
              {{ stage.total }}
            </span>
            <span class="text-green-600 dark:text-green-400">
              {{ stage.won }} {{ t('DEAL_REPORTS.FUNNEL.WON') }}
            </span>
            <span class="text-red-600 dark:text-red-400">
              {{ stage.lost }} {{ t('DEAL_REPORTS.FUNNEL.LOST') }}
            </span>
            <span class="text-amber-600 dark:text-amber-400">
              {{ stage.open }} {{ t('DEAL_REPORTS.FUNNEL.OPEN') }}
            </span>
            <span class="text-slate-500 dark:text-slate-400">
              {{ stage.conversion_rate }}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="h-48 flex items-center justify-center">
      <p class="text-slate-500 dark:text-slate-400">
        {{ t('DEAL_REPORTS.NO_DATA') }}
      </p>
    </div>

    <!-- Legend -->
    <div class="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
      <div class="flex flex-wrap gap-4 text-xs">
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-green-500" />
          <span class="text-slate-600 dark:text-slate-400">{{
            t('DEAL_REPORTS.FUNNEL.WON')
          }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-red-500" />
          <span class="text-slate-600 dark:text-slate-400">{{
            t('DEAL_REPORTS.FUNNEL.LOST')
          }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-amber-500" />
          <span class="text-slate-600 dark:text-slate-400">{{
            t('DEAL_REPORTS.FUNNEL.OPEN')
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
